import {
    TRPCLink,
    createTRPCProxyClient,
    httpBatchLink,
    loggerLink,
    httpLink,
    splitLink,
} from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import { type AppRouter } from "~/server/api/root";
import { env } from "~/env.mjs";

// // // //

function getBaseUrl() {
    if (typeof window !== "undefined")
        // browser should use relative path
        return "";

    if (env.VERCEL_URL)
        // reference for vercel.com
        return `https://${env.VERCEL_URL}`;

    if (env.RENDER_INTERNAL_HOSTNAME)
        // reference for render.com
        return `http://${env.RENDER_INTERNAL_HOSTNAME}:${env.PORT}`;

    // assume localhost
    return `http://localhost:${env.PORT ?? 3000}`;
}

// // // //

// Defines links for tRPC API + Proxy Client
// Doc: https://trpc.io/docs/links

const links: TRPCLink<AppRouter>[] = [
    loggerLink({
        enabled: (opts) =>
            process.env.NODE_ENV === "development" ||
            (opts.direction === "down" && opts.result instanceof Error),
    }),
    // Disable batching for certain requests
    // Doc: https://trpc.io/docs/v9/links#disable-batching-for-certain-requests
    // NOTE - we add this so the `getProfile` query for the user auth handshake is NOT batched with other requests
    splitLink({
        condition(op) {
            // check for context property `skipBatch`
            return op.context.skipBatch === true;
        },
        // when condition is true, use normal request
        true: httpLink({
            url: `${getBaseUrl()}/api/trpc`,
        }),
        // when condition is false, use batching
        false: httpBatchLink({
            url: `${getBaseUrl()}/api/trpc`,
        }),
    }),
];

/**
 * Initializes the tRPC API.
 * Used when interacting with tRPC inside our React components
 * NOTE - must be rendered beneath `TRPCReactProvider` (~/modules/app/components/TRPCReactProvider)
 */
export const api = createTRPCReact<AppRouter>();

/**
 * Creates the tRPC client passed to `api.Provider`.
 * Used by `TRPCReactProvider` - call once per mounted provider, NOT at module scope
 */
export function createTRPCReactClient() {
    return api.createClient({
        /**
         * Transformer used for data de-serialization from the server.
         *
         * @see https://trpc.io/docs/data-transformers
         */
        // transformer: superjson,

        /**
         * Links used to determine request flow from client to server.
         *
         * @see https://trpc.io/docs/links
         */
        links: [...links],
    });
}

/**
 * Creates a tRPC proxy client.
 * Used when interacting with tRPC *outside* our React components
 */
export const proxyClient = createTRPCProxyClient<AppRouter>({
    links: [...links],
});
